import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ChevronLeft, ChevronRight, Loader2, Trophy, Zap, Sparkles } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PipelineStepper, { ADMIN_DEV_STEP_META } from "@/components/simulator/PipelineStepper";
import StepFeedback from "@/components/simulator/StepFeedback";
import IdentityStep from "@/components/simulator/IdentityStep";
import SingleChoiceStep from "@/components/simulator/SingleChoiceStep";

const STEPS = ["objects", "security", "automation", "apex_deploy"];

const STEP_COPY = {
  objects: { title: "Step 1 — Data Model & Custom Objects", description: "Select every Standard/Custom Object and relationship this scenario actually needs. Watch out for decoys that sound plausible but aren't required." },
  security: { title: "Step 2 — Security & Sharing Model", description: "Select the OWD/Role Hierarchy/Sharing Rule combination that gives the right people visibility — no more, no less." },
  automation: { title: "Step 3 — Automation Tool Choice", description: "Pick the single best-fit automation tool for this specific requirement." },
  apex_deploy: { title: "Step 4 — Apex & Deployment Best Practices", description: "Select every best practice this scenario should follow for safe, bulk-safe, well-tested code." },
};

const emptyAnswers = () => ({ objects: [], security: [], automation: null, apex_deploy: [] });

export default function AdminDevSimulatorScenarioPage() {
  const { scenarioId } = useParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [scenario, setScenario] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [stepIndex, setStepIndex] = useState(0);
  const [answers, setAnswers] = useState(emptyAnswers());
  const [results, setResults] = useState({});
  const [checking, setChecking] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [completion, setCompletion] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError("");
    setStepIndex(0);
    setAnswers(emptyAnswers());
    setResults({});
    setCompletion(null);
    api.get(`/admin-dev-simulator/scenarios/${scenarioId}`).then(({ data }) => {
      setScenario(data);
      const done = data.steps_completed || {};
      const nextAnswers = emptyAnswers();
      const nextResults = {};
      for (const step of STEPS) {
        if (!done[step]) continue;
        const ans = done[step].answer || {};
        if (step === "automation") nextAnswers.automation = ans.selected_id || null;
        else nextAnswers[step] = ans.selected_ids || [];
        nextResults[step] = { score: done[step].score, ai_feedback: done[step].ai_feedback, is_correct: done[step].score >= 80 };
      }
      setAnswers(nextAnswers);
      setResults(nextResults);
    }).catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || "Unable to load this simulation"))
      .finally(() => setLoading(false));
  }, [scenarioId]);

  const currentStep = STEPS[stepIndex];

  const buildPayload = () => {
    if (currentStep === "automation") return { selected_id: answers.automation };
    return { selected_ids: answers[currentStep] };
  };

  const checkStep = async () => {
    setChecking(true);
    try {
      const { data } = await api.post(`/admin-dev-simulator/scenarios/${scenarioId}/steps/${currentStep}`, buildPayload());
      setResults((prev) => ({ ...prev, [currentStep]: data }));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to check this step");
    } finally {
      setChecking(false);
    }
  };

  const completeSimulation = async () => {
    setCompleting(true);
    try {
      const { data } = await api.post(`/admin-dev-simulator/scenarios/${scenarioId}/complete`);
      setCompletion(data);
      refreshUser();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't complete the simulation — please try again.");
    } finally {
      setCompleting(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center" data-testid="admin-dev-sim-scenario-loading"><div className="w-10 h-10 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" /></div>;
  }
  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-6 text-center" data-testid="admin-dev-sim-scenario-error">
        <p className="text-slate-300">{error}</p>
        <Link to="/admin-dev-simulator" className="text-amber-400 hover:text-amber-300 text-sm">Back to Simulator</Link>
      </div>
    );
  }

  if (completion) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-16 text-center" data-testid="admin-dev-sim-completion-screen">
        <Trophy className="w-16 h-16 text-[#FBBF24] mx-auto mb-4" />
        <h1 className="font-heading text-3xl font-semibold text-white">Simulation Complete!</h1>
        <p className="text-slate-400 mt-2">{scenario.title}</p>
        <div className="flex items-center justify-center gap-8 mt-8">
          <div className="text-center"><p className="font-heading text-3xl font-bold text-amber-400" data-testid="admin-dev-completion-total-score">{completion.total_score}%</p><p className="text-xs text-slate-400 mt-1">Total Score</p></div>
          <div className="text-center"><p className="font-heading text-3xl font-bold text-[#FBBF24] flex items-center gap-1 justify-center"><Zap className="w-6 h-6" />{completion.xp_awarded}</p><p className="text-xs text-slate-400 mt-1">XP Earned</p></div>
        </div>
        <div className="flex items-center justify-center gap-3 mt-8">
          <button onClick={() => navigate("/admin-dev-simulator")} data-testid="admin-dev-completion-back-to-list-btn" className="text-sm font-semibold px-5 py-2.5 rounded-lg bg-white/10 hover:bg-white/15 text-white transition-colors">
            Back to Simulator
          </button>
          {completion.next_scenario_id && (
            <button onClick={() => navigate(`/admin-dev-simulator/${completion.next_scenario_id}`)} data-testid="admin-dev-completion-next-scenario-btn" className="text-sm font-semibold px-5 py-2.5 rounded-lg bg-amber-500 hover:bg-amber-400 text-white transition-colors">
              Next Scenario →
            </button>
          )}
        </div>
      </div>
    );
  }

  const result = results[currentStep];
  const isLast = stepIndex === STEPS.length - 1;
  const allStepsChecked = STEPS.every((s) => !!results[s]);
  const copy = STEP_COPY[currentStep];

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8" data-testid="admin-dev-sim-scenario-page">
      <button onClick={() => navigate("/admin-dev-simulator")} data-testid="admin-dev-back-to-simulator-btn" className="flex items-center gap-2 text-sm text-slate-400 hover:text-white mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to Simulator
      </button>

      <div className="glass-panel rounded-2xl p-6 mb-6">
        <span className="text-xs font-bold uppercase tracking-[0.2em] text-amber-400">{scenario.industry}</span>
        <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-white mt-2" data-testid="admin-dev-sim-scenario-title">{scenario.title}</h1>
        <p className="text-sm text-slate-400 mt-2">{scenario.description}</p>
      </div>

      <PipelineStepper steps={STEPS} currentStep={currentStep} results={results} onJump={setStepIndex} stepMeta={ADMIN_DEV_STEP_META} />

      <div className="glass-panel rounded-2xl p-6">
        {currentStep === "automation" ? (
          <SingleChoiceStep options={scenario.automation.options} selectedId={answers.automation} onChange={(v) => setAnswers((p) => ({ ...p, automation: v }))} title={copy.title} description={copy.description} />
        ) : (
          <IdentityStep options={scenario[currentStep].options} selected={answers[currentStep]} onChange={(v) => setAnswers((p) => ({ ...p, [currentStep]: v }))} title={copy.title} description={copy.description} />
        )}

        <StepFeedback result={result} />

        <div className="flex items-center justify-between mt-6 pt-6 border-t border-white/5">
          <button
            onClick={() => setStepIndex((i) => Math.max(0, i - 1))} disabled={stepIndex === 0} data-testid="admin-dev-sim-prev-step-btn"
            className="flex items-center gap-1.5 text-sm font-semibold text-slate-400 hover:text-white disabled:opacity-30 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" /> Previous
          </button>
          <button
            onClick={checkStep} disabled={checking} data-testid="admin-dev-sim-check-step-btn"
            className="flex items-center gap-2 bg-white/10 hover:bg-white/15 disabled:opacity-60 text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors"
          >
            {checking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {result ? "Re-check with AI" : "Check My Work"}
          </button>
          {isLast ? (
            <button
              onClick={completeSimulation} disabled={completing || !allStepsChecked} data-testid="admin-dev-sim-complete-btn"
              className="flex items-center gap-1.5 text-sm font-semibold bg-amber-500 hover:bg-amber-400 disabled:opacity-40 text-white px-5 py-2.5 rounded-lg transition-colors"
            >
              {completing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trophy className="w-4 h-4" />} Complete Simulation
            </button>
          ) : (
            <button
              onClick={() => setStepIndex((i) => Math.min(STEPS.length - 1, i + 1))} data-testid="admin-dev-sim-next-step-btn"
              className="flex items-center gap-1.5 text-sm font-semibold text-slate-400 hover:text-white transition-colors"
            >
              Next <ChevronRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
